import ArgumentException from '../../exceptions/ArgumentException';

export function useDate () {

  const isDate = (date: Date | string): date is Date => {
    return date instanceof Date;
  };

  const isValidDate = (date: string): boolean => {
    return !isNaN(Date.parse(date));
  };

  const getHyphenatedDateFromDateString = (date: string): string => {
    return date === '0000-00-00 00:00:00' ? '--' : date.substring(0, 10);
  };
  const getTime24HrFromDateString = (date: string): string => {
    return !date || date === '0000-00-00 00:00:00' ? '--' : date.substring(11, 16);
  };
  const pickupDateRequest = (cartPickupTime: string): string => {
   return cartPickupTime !== '0000-00-00 00:00:00' ? getHyphenatedDateFromDateString(cartPickupTime) : undefined;
  };
  const getDateIdFromDateString = (date: string): number => {
    // REPLACES NON-DIGITS AND REMOVES SECONDS FROM DATE STRING
    return parseInt(date.replace(/\D/g, '').slice(0, -2));
  };
  const getDateStringFromDateId = (id: number): string => {
    const regex = /(\d{4})(\d{2})(\d{2})/g;
    const dateData = regex.exec(id.toString());
    return dateData[1] + '-' + dateData[2] + '-' + dateData[3];
  };
  const buildDateFromString = (dateString: string): Date => {
    if (!isValidDate(dateString)) {
      throw new ArgumentException('Invalid Date String ' + dateString);
    }
    return new Date(dateString.replace(/-/g, '/'));
  };

  const formatWithFullMonthName = (dateString: string): string => {
    if(!dateString) { return 'N/A'; }
    if(typeof dateString === 'string' && !isValidDate(dateString)) { return 'N/A'; }

    const date = isDate(dateString) ? dateString : buildDateFromString(dateString);

    return date.toLocaleDateString(undefined, {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
  };

  const addDaysToDate = (date: string, numDays: number): string => {
    const dateObject = new Date(date);
    const nextDayOfMonth = dateObject.getDate() + numDays;
    dateObject.setDate(nextDayOfMonth);
    return dateObject.toLocaleString();
  };

  const formatToLocale = (dateString: string, simple=false): string => {
    if(!dateString) { return 'N/A'; }
    if(typeof dateString === 'string' && !isValidDate(dateString)) { return 'N/A'; }

    const date = isDate(dateString) ? dateString : buildDateFromString(dateString);

    return (new Date(date)).toLocaleDateString(undefined, {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      ...[
          simple && {second: 'numeric'},
          simple && {timeZoneName: 'short'},
      ],
    });
  };

  const getHourMinutes24HrFormat = (date: Date): string => {
    const hour = date.getHours().toLocaleString(undefined, {
      minimumIntegerDigits: 2,
    });
    const minutes = date.getMinutes().toLocaleString(undefined, {
      minimumIntegerDigits: 2,
    });
    return hour + ':' + minutes;
  };

  const getDateAndTimeMDY2DigitFormat = (dateString: string): string => {
    if(!dateString) { return 'N/A'; }
    const date = isDate(dateString) ? dateString : buildDateFromString(dateString);
    const time = getHourMinutes24HrFormat(date);

    const dateFormatted = new Date(date).toLocaleDateString(undefined, {
      month: 'numeric',
      day: 'numeric',
      year: '2-digit',
    });

    return dateFormatted + ' ' + time;
  };
  const time24HrTo12Hr = (timeString: string): string => {
    return new Date('1970-01-01T' + timeString + 'Z')
        .toLocaleTimeString('en-US', {
          timeZone:'UTC',
          hour12:true,
          hour:'2-digit',
          minute:'numeric',
        });
  };

  const formatToYYYYmmdd = (date: Date): string => {
    if(!(date instanceof Date)) {
      throw new ArgumentException('Invalid date type');
    }

    const year = date.getFullYear();
    const month = `${date.getMonth() + 1}`.padStart(2, '0');
    const day = date.getDate();

    return `${year}-${month}-${day}`;
  };

  return {
    isValidDate,
    addDaysToDate,
    formatToLocale,
    formatWithFullMonthName,
    getDateAndTimeMDY2DigitFormat,
    getHyphenatedDateFromDateString,
    getTime24HrFromDateString,
    getDateIdFromDateString,
    getDateStringFromDateId,
    pickupDateRequest,
    time24HrTo12Hr,
    formatToYYYYmmdd,
  };
}
